import {
  Badge,
  Box,
  Button,
  Card,
  CardHeader,
  Flex,
  Grid,
  Heading,
  Select,
  Skeleton,
  Spinner,
  Text
} from '@chakra-ui/react'
import { useWindowWidth } from '@react-hook/window-size'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { Risk } from 'BrokerTsLib/src/enums'
import uniq from 'lodash/uniq'
import { useEffect, useRef, useState } from 'react'

import { LoadingOverlay } from '@/components'
import { LeasingBasicParametersFormWrapper } from '@/components/LeasingBasicParametersFormWrapper'
import { LeasingHeader } from '@/components/LeasingHeader'
import { PageSection as PageLayout } from '@/components/PageSection'
import { QuoteItem } from '@/components/QuoteItem'
import { QuotesFilter } from '@/components/QuotesFilter'
import { BadgeStep } from '@/components/ui/BadgeStep'
import { Container } from '@/components/ui/Container'
import { Section } from '@/components/ui/Section'
import { WizzardHeader } from '@/components/WizzardHeader'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { SortBy } from '@/context/OffersProvider'
import {
  useOffersVehicleContext
} from '@/context/OffersVehicleProvider'
import { usePrintContext } from '@/context/PrintContext'
import { useSelectCalculation } from '@/hooks/useOffer'
import { Check } from '@/icons'
import { scrollToElement } from '@/utils/scrollToElement'

export const LeasingPageComponent = () => {
  const { insuranceParams: ip, refetchInsuranceParams, insuranceParamsId } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsDto
  const { printMode, type } = usePrintContext()
  const { mutateAsync: selectOffer } = useSelectCalculation()

  const {
    refetchOffers: getOffers,
    setOfferIncluded,
    offers,
    isLoading,
    calculatingOffersIds,
    sortBy,
    setSortBy: changeSortBy,
    showAll,
    setShowAll: changeShowAll,
    filtered,
    handleIncludeFiltered,
  } = useOffersVehicleContext()

  const [defaultFullHeight, setDefaultFullHeight] = useState<number | undefined>(148 + 32)
  const [showFilter, setShowFilter] = useState(false)
  const [calculationState, setCalculationState] = useState<{
    isCalculated: boolean
    isCalculating: boolean
  }>({ isCalculated: false, isCalculating: false })
  const isCalculatingAny = isLoading || (calculatingOffersIds?.length ?? 0) > 0

  const headerRef = useRef<HTMLDivElement>(null)
  const windowWidth = useWindowWidth()

  // pro pouziti useEfectu nize
  const allOffersRiskVariants = uniq(
    // distinct
    (insuranceParams?.baseParams?.variants ?? []).reduce(
      (pn: number[], insuranceVariant) => [
        ...pn,
        ...(insuranceVariant.variantParts
          ?.filter((x) =>
            [Risk.POV, Risk.HAV, Risk.SPOL, Risk.GAP].includes(x.risk),
          )
          .map((x) => x.riskVariant ?? 0) ?? []),
      ],
      [],
    ), // selectMany
  )

  // pri docteni nove offery s novou riskVariantou musim refetchnout paramsy, aby se spravne zobrazil filter (filtr se plni na zaklade vygenerovanych variant, ktere jsou na paramsech)
  useEffect(() => {
    if (allOffersRiskVariants?.length > 0) refetchInsuranceParams()
  }, [allOffersRiskVariants?.length])

  // při kalkulaci refetchout parametry, aby se načetly změny způsobené kalkulací
  useEffect(() => {
    if (calculationState.isCalculating || calculationState.isCalculated)
      refetchInsuranceParams()
  }, [calculationState.isCalculating])

  useEffect(() => {
    if (insuranceParams?.generalParams.lastWizardStep == 2) handleCalculate()
  }, [])

  useEffect(() => {
    const element = headerRef?.current
    const height = element?.getBoundingClientRect().height
    const top = element?.getBoundingClientRect().top

    if (height) setDefaultFullHeight(height + Number(top))
  }, [headerRef])

  useEffect(() => {
    if (windowWidth > 768) {
      // medium breakpoint
      document.body.style.overflow = 'auto'
      return
    }

    document.body.style.overflow = showFilter ? 'hidden' : 'auto'
  }, [showFilter, windowWidth])

  useEffect(() => {
    if (offers.length > 0) {
      setCalculationState({
        isCalculated: true,
        isCalculating: false,
      })
    }
  }, [offers.length, isCalculatingAny])

  const [firstLoad, setFirstLoad] = useState(true)
  useEffect(() => {
    if (firstLoad) setFirstLoad(false)
    else return

    if (insuranceParams?.generalParams.existsOffers)
      getOffers({})

    setTimeout(() => {
      scrollToOffers()
    }, 100)
  }, [insuranceParams?.generalParams.existsOffers, firstLoad])

  const handleShowFilter = () => {
    setShowFilter(!showFilter)
  }

  const handleAddNewVersion = () => {
    scrollToElement({ elementId: 'sekce-1' })
  }

  const scrollToOffers = () => {
    scrollToElement({ elementId: 'sekce-2', offset: 64 })
  }

  const handleCalculate = () => {
    // ziskanni / zajisteni vytvoreni offers
    setCalculationState({
      isCalculated: false,
      isCalculating: true,
    })

    getOffers({ force: true }).then(({ data }) => {
      if (data?.length == 0) {
        setCalculationState({
          isCalculated: true,
          isCalculating: false,
        })
      }
    })

    setTimeout(() => {
      scrollToOffers()
    }, 100) // 100ms delay to wait for section to be added to DOM
  }

  const selectedOffer = filtered.find((quote) => quote.offerId == insuranceParams?.generalParams.selectedOfferId)
  const selectedOffers = offers.filter(x => x.selected)

  return (
    <>
      {printMode && <WizzardHeader />}

      {!printMode && (
        <LeasingHeader
          ref={headerRef}
          brand={insuranceParams?.vehicle.znacka}
          model={insuranceParams?.vehicle.model}
        />
      )}

      {/* Header height placeholder */}
      {!printMode && (
        <Box
          sx={{
            display: { base: 'none', md: 'block' },
            position: 'static',
            pointerEvents: 'none',
            transition: 'all 200ms ease-in-out',
          }}
          style={{
            height: `${defaultFullHeight}px`,
          }}
        />
      )}

      <Section>
        <Container>
          <Grid
            sx={{
              position: 'relative',
              gridTemplateColumns: { md: '250px 2rem 1fr' },
              gap: 0,
            }}
          >
            <Grid
              as="aside"
              sx={{ position: 'relative', alignContent: 'start' }}
            >
              {calculationState.isCalculated && (
                <Grid
                  sx={{
                    position: { md: 'sticky' },
                    top: 28,
                    maxH: { md: '100vh' },
                    py: 4,
                  }}
                  style={{
                    marginTop:
                      windowWidth > 768
                        ? (document.getElementById('sekce-2')?.offsetTop ?? 0)
                        : '0',
                  }}
                >
                  <QuotesFilter
                    showFilter={showFilter}
                    onCloseClick={handleShowFilter}
                    onIncludeFiltered={handleIncludeFiltered}
                  />
                </Grid>
              )}
            </Grid>

            <Box
              sx={{
                width: '3px',
                height: '100%', // 8px is the top margin of the line
                bg: 'white',
                // mt: 2,
                ml: '-3px',
                borderRadius: 'full',
              }}
            />

            <Grid sx={{ alignContent: 'start', alignItems: 'start', gap: 8 }}>
              {!printMode && (
                <PageLayout
                  id="sekce-1"
                  isLast={
                    !calculationState.isCalculated &&
                    !calculationState.isCalculating
                  }
                >
                  {insuranceParams && (
                    <LeasingBasicParametersFormWrapper
                      insuranceParams={insuranceParams}
                      onSubmit={handleCalculate}
                    />
                  )}
                </PageLayout>
              )}

              {(calculationState.isCalculated === true || calculationState.isCalculating === true) && (
                <PageLayout id="sekce-2" mainSx={{ gap: 8 }} isLast>
                  {!printMode && <>
                    <Card>
                      <CardHeader sx={{ py: 5 }}>
                        <Flex
                          sx={{
                            gap: { base: 2, md: 8 },
                            flexWrap: 'wrap',
                            alignItems: 'center',
                          }}
                        >
                          <BadgeStep>
                            {isCalculatingAny ? <Spinner size="xs" /> : 2}
                          </BadgeStep>

                          <Heading
                            sx={{
                              minWidth: '170px', // width of element when calculating text is shown
                              fontSize: 'lg',
                              fontWeight: 'bold',
                            }}
                          >
                            {isCalculatingAny
                              ? 'Kalkuluji pojištění...'
                              : 'Kalkulace pojištění'}
                          </Heading>

                          <Skeleton isLoaded={calculationState.isCalculated}>
                            <Box>
                              <Select
                                variant="filled"
                                size="sm"
                                value={sortBy}
                                onChange={(e) =>
                                  changeSortBy!(e.target.value as SortBy)
                                }
                              >
                                <option value={SortBy.Price}>
                                  Dle pojistného
                                </option>
                                <option value={SortBy.Insurer}>
                                  Dle pojišťovny
                                </option>
                              </Select>
                            </Box>
                          </Skeleton>

                          <Grid
                            sx={{
                              placeContent: 'center',
                              placeItems: 'center',
                            }}
                          >
                            <Skeleton
                              isLoaded={calculationState.isCalculated}
                            >
                              <Badge
                                sx={{ fontSize: 'xs', fontWeight: 'bold' }}
                              >
                                zobrazeno {filtered.length}/{offers.length}
                              </Badge>
                            </Skeleton>

                            {/* <Text sx={{ fontSize: 'xs' }}>zobrazeno/celkem</Text> */}
                          </Grid>

                          <Flex
                            sx={{
                              gap: 4,
                              alignItems: 'center',
                              ml: { md: 'auto' },
                            }}
                          >
                            <Skeleton
                              isLoaded={calculationState.isCalculated}
                            >
                              <Button
                                variant="link"
                                onClick={() => changeShowAll(!showAll)}
                              >
                                {showAll ? 'Skrýt nevybrané' : 'Zobrazit vše'}
                              </Button>
                            </Skeleton>

                            <Box>
                              <Skeleton
                                isLoaded={calculationState.isCalculated}
                              >
                                <Button onClick={handleAddNewVersion}>
                                  Přidat další variantu pojištění
                                </Button>
                              </Skeleton>
                            </Box>
                          </Flex>
                        </Flex>
                      </CardHeader>
                    </Card>


                    {filtered.length == 0 && (
                      <Box sx={{ minHeight: '80vh' }}>
                        {calculationState.isCalculated && !calculationState.isCalculating && (
                          <Text fontWeight="bold" textAlign={'center'}>
                            Pro vybrané kritéria nebyly zkalkulováný žádné
                            nabídky
                          </Text>
                        )}
                      </Box>
                    )}
                  </>}

                  {calculationState.isCalculated &&
                    filtered &&
                    filtered.length > 0 &&
                    [
                      ...filtered.filter(x => x.offerId == selectedOffer?.offerId),
                      ...filtered.filter(x => x.offerId != selectedOffer?.offerId)
                    ]
                      .map((offer) => {
                        return (
                          <LoadingOverlay key={offer.offerId}>
                            <QuoteItem
                              isSelected={
                                insuranceParams?.generalParams.selectedOfferId ==
                                offer.offerId
                              }
                              offerId={offer.offerId}
                              onIncludedChange={setOfferIncluded}
                            />
                          </LoadingOverlay>
                        )
                      })}

                  {selectedOffers.length > 0 && (
                    <Flex
                      justifyContent='center'>
                      <Button
                        colorScheme="green"
                        size="lg"
                        onClick={() => {
                          // TODO: 
                        }}>
                        <Check color="white.500" />
                        Sjednat vybrané
                      </Button>
                    </Flex>
                  )}
                </PageLayout>
              )}
            </Grid>
          </Grid>
        </Container>
      </Section>
    </>
  )
}